import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useAppDispatch, useAppSelector } from '../../Redux/Store/Store';
import { getClient, getClientHasThirdPartySubsets, getCanAccessDevelop } from '../../Redux/Store/Slices/client';
import { GetPagedDevelopAreaDisplayInfosByClientAndUser } from '../../Redux/Store/Slices/area';

import PagedCardCollection from '../../Components/Utils/LandingPages/Evaluate/PagedCardCollection';
import BulkExportWizard from '../../Components/Utils/LandingPages/BulkExportWizard/BulkExportWizard';
import CompactFilters, { ISelectOption } from '../../Components/Analyse/CompactFilters/CompactFilters';
import { dateRangeValue, namesToSortedOptions } from '../../Components/Analyse/CompactFilters/filterUtils';
import FilterSearchBar from '../../Components/SearchBar/FilterSearchBar/FilterSearchBar';
import Button from '../../Components/Utils/Button/Button';
import Tooltip from '../../Components/Utils/Tooltip/Tooltip';

import { AreaDisplayInfo } from '../../Types/AreaDisplayInfo/AreaDisplayInfo';
import { AnalyseDataRequest } from '../../Types/Paging/PagedRequest';
import { PagedResponse } from '../../Types/Paging/PagedResponse';

import styles from '../Evaluate/LandingPages.module.css';
import { SetArchivedEvaluateAreaRequest } from '../../Types/Evaluate/SetArchivedEvaluateAreaRequest';
import { axiosInstance } from '../../Authentication/AxiosInterceptor';

// Constants
const roleOptions: ISelectOption[] = [
    { label: 'Participant', value: 'Participant', required: false },
    { label: 'Reader', value: 'Reader', required: false },
    { label: 'Owner', value: 'Owner', required: false },
    { label: 'Lead', value: 'Lead', required: false },
];

type DevelopActiveProps = {
    setShowImportTemplate: (show: boolean) => void;  
    archived: boolean;
};
  
const DevelopActive = ({ setShowImportTemplate, archived }: DevelopActiveProps) => {
    const dispatch = useAppDispatch();
    
    const canAccessDevelop = useAppSelector(getCanAccessDevelop);
    const client = useAppSelector(getClient);
    const clientId = client?.id;
    const hasThirdPartySubsets = useAppSelector(getClientHasThirdPartySubsets);

    const [selectedDepartments, setSelectedDepartments] = useState<ISelectOption[]>([]);
    const [selectedGroups, setSelectedGroups] = useState<ISelectOption[]>([]);
    const [selectedObservees, setSelectedObservees] = useState<ISelectOption[]>([]);
    const [selectedOwners, setSelectedOwners] = useState<ISelectOption[]>([]);
    const [selectedTemplateTypes, setSelectedTemplateTypes] = useState<ISelectOption[]>([]);
    const [selectedRoles, setSelectedRoles] = useState<ISelectOption[]>([]);
    const [completedDate, setCompletedDate] = useState<ISelectOption[]>([]);

    const [displayInfos, setDisplayInfos] = useState<AreaDisplayInfo[]>([]);
    const [searchTerm, setSearchTerm] = useState<string>('');
    const [loading, setLoading] = useState<boolean>(true);
    const [bulkExportOpen, setBulkExportOpen] = useState(false);
    const [exportPreselectedId, setExportPreselectedId] = useState<string | undefined>();

    const openBulkExport = (areaId?: string) => {
        setExportPreselectedId(areaId);
        setBulkExportOpen(true);
    };

    const closeBulkExport = () => {
        setBulkExportOpen(false);
        setExportPreselectedId(undefined);
    };

    const fetchData = useCallback(async () => {
        setLoading(true);

        const request: AnalyseDataRequest = {
            partnerIds: [],
            departmentIds: [],
            groupIds: [],
            role: "",
            archived: archived,
            softFilter: true,
            pageNumber: 1,
            pageSize: 100000
        };

        const result = await dispatch(GetPagedDevelopAreaDisplayInfosByClientAndUser(request));
        const payload = result.payload as PagedResponse<AreaDisplayInfo>;
        const items = payload.items || [];
        
        setDisplayInfos(items);
        setLoading(false);
    }, [dispatch, archived]);

    useEffect(() => {
        if (clientId && clientId !== 'undefined' && clientId !== 'null') {
            fetchData();
        }
    }, [clientId, fetchData]);

    const handleSearchTermChange = (value: string) => {
        setSearchTerm(value);
    };

    const toggleArchiveHandler = async (id: string) => {
        await axiosInstance.post(`SetArchivedEvaluateAreaById`, {
            areaId: id,
            archived: !archived
        } as SetArchivedEvaluateAreaRequest);
        
        fetchData();
    };

    const bulkSelectEnabled = displayInfos.some((a) => a.canExport);

    const observeeOptions = useMemo<ISelectOption[]>(
        () => namesToSortedOptions(displayInfos.map(info => info.observee || info.individual || '')),
        [displayInfos]
    );

    const ownerOptions = useMemo<ISelectOption[]>(
        () => namesToSortedOptions(displayInfos.map(info => info.ownerName || '')),
        [displayInfos]
    );

    const filterConfig = useMemo(() => [
        { key: 'observees', label: 'Completed For', placeholder: 'Select users', dataSource: 'props' as const, data: observeeOptions },
        { key: 'owners', label: 'Completed By', placeholder: 'Select users', dataSource: 'props' as const, data: ownerOptions },
        { key: 'completedDate', label: 'Date Completed', placeholder: 'Pick a date range', kind: 'dateRange' as const },
        { key: 'departments', label: 'Departments', placeholder: 'Select departments', dataSource: 'api' as const },
        { key: 'groups', label: 'Groups', placeholder: 'Select groups', dataSource: 'api' as const },
        { key: 'templateTypes', label: 'Types', placeholder: 'Select types', dataSource: 'props' as const, data: [] },
        { key: 'roles', label: 'Roles', placeholder: 'Select roles', dataSource: 'props' as const, data: roleOptions },
    ], [observeeOptions, ownerOptions]);

    const selectedFilters = useMemo(() => ({
        observees: selectedObservees,
        owners: selectedOwners,
        completedDate,
        departments: hasThirdPartySubsets ? selectedDepartments : [],
        groups: selectedGroups,
        templateTypes: selectedTemplateTypes,
        roles: selectedRoles,
    }), [selectedObservees, selectedOwners, completedDate, hasThirdPartySubsets, selectedDepartments, selectedGroups, selectedTemplateTypes, selectedRoles]);

    const completedRange = useMemo(() => dateRangeValue(completedDate), [completedDate]);

    const handleFilterChange = (filters: Record<string, ISelectOption[]>) => {
        setSelectedObservees(filters.observees || []);
        setSelectedOwners(filters.owners || []);
        setCompletedDate(filters.completedDate || []);
        setSelectedDepartments(filters.departments || []);
        setSelectedGroups(filters.groups || []);
        setSelectedTemplateTypes(filters.templateTypes || []);
        setSelectedRoles(filters.roles || []);
    };

    return (
        <div className={styles.listings}>
            <BulkExportWizard
                isVisible={bulkExportOpen}
                onClose={closeBulkExport}
                areas={displayInfos}
                productName="Develop"
                initialSelectedIds={exportPreselectedId ? [exportPreselectedId] : undefined}
            />
            <div className={styles.topContainer}>
                <FilterSearchBar
                    value={searchTerm}
                    onChange={handleSearchTermChange}
                    filters={filterConfig}
                    selectedFilters={selectedFilters}
                    onFilterChange={handleFilterChange}
                />
                <div className={styles.top}>
                    <CompactFilters
                        filters={filterConfig}
                        selectedFilters={selectedFilters}
                        onFilterChange={handleFilterChange}
                    />

                    <div className={styles.actionButtons}>
                        {bulkSelectEnabled && (
                            <Button
                                colour="rgb(36, 57, 79)"
                                textColour="#FFF"
                                clickHandler={() => openBulkExport()}
                            >
                                Export data
                            </Button>
                        )}
                        <Tooltip 
                        text="You need a Develop licence to create a diagnostic" 
                        align="top" 
                        sideAlign="left" 
                        disabled={canAccessDevelop}
                    >
                        <div className={styles.createButton}>
                                <Button 
                                    disabled={!canAccessDevelop} 
                                    colour="rgb(36, 57, 79)" 
                                    textColour="#FFF" 
                                    clickHandler={() => setShowImportTemplate(true)}
                                >
                                    Create new diagnostic
                                </Button>
                        </div>
                    </Tooltip>
                    </div>
                </div>
            </div>

            <PagedCardCollection  
                template={false}
                displayInfos={displayInfos}
                archived={archived}
                toggleArchiveHandler={toggleArchiveHandler}
                loading={loading}
                selectedRoleOption={selectedRoles}
                selectedPartners={[]}
                selectedDepartments={selectedDepartments}
                selectedGroups={selectedGroups}
                selectedObservees={selectedObservees}
                selectedOwners={selectedOwners}
                completedFrom={completedRange.from}
                completedTo={completedRange.to}
                searchTerm={searchTerm}
                selectable={false}
                hideExportButton
                onCardExport={bulkSelectEnabled ? openBulkExport : undefined}
            />
        </div>
    );  
};  
  
export default DevelopActive;
